import Input from 'antd/es/input/Input';
import { Checkbox, Form, Radio, Rate, Select, Space, theme } from 'antd';
import { Control, Controller, UseFormSetValue } from 'react-hook-form';
import {
  ChoiceEntity,
  ListEntityV2,
  QuestionItem,
  QuestionStructureType,
  QuestionType,
  SurveyExtensionPoint,
  questionChoicesKeys,
} from '@seaters/constants';
import {
  getSingleRangeOptionTranslation,
  getSingleTranslation,
  queryClient,
} from '@seaters/data-access';
import { CustomCarousel } from '../components/CustomCarousel';
import { t } from 'i18next';
import {
  getRangeOptionIdFromRateValue,
  getRangeRateLabel,
  getRangeRateValue,
} from './rangeRate.helpers';

import './styles.css';

const STRUCTURED_LABEL_KEYS: Record<
  string,
  { key: string; defaultValue: string }
> = {
  firstName: {
    key: 'survey_structured_first_name',
    defaultValue: 'First name',
  },
  lastName: {
    key: 'survey_structured_last_name',
    defaultValue: 'Last name',
  },
  email: { key: 'survey_structured_email', defaultValue: 'Email' },
  birthDate: {
    key: 'survey_structured_birth_date',
    defaultValue: 'Birth date',
  },
  idNumber: {
    key: 'survey_structured_id_number',
    defaultValue: 'ID number',
  },
  company: { key: 'survey_structured_company', defaultValue: 'Company' },
  phoneNumber: {
    key: 'survey_structured_phone_number',
    defaultValue: 'Phone number',
  },
  address: { key: 'survey_structured_address', defaultValue: 'Address' },
  language: { key: 'survey_structured_language', defaultValue: 'Language' },
};

const getStructuredFieldLabel = (name: string): string => {
  const preset = STRUCTURED_LABEL_KEYS[name];
  if (preset) {
    return t(preset.key, { defaultValue: preset.defaultValue });
  }
  return name;
};

const renderStructured = (
  questionItem: QuestionItem,
  control: Control<any, any>,
  ticket: string
) => {
  return questionItem.question.structure?.map((structuredQuestionItem) => {
    const fieldLabel = getStructuredFieldLabel(structuredQuestionItem.name);
    return structuredQuestionItem.type !== QuestionStructureType.CHECK_BOX ? (
      <Form.Item
        name={`${questionItem.question.id}.${ticket}.${structuredQuestionItem.name}`}
        label={fieldLabel}
        required={structuredQuestionItem.mandatory}
      >
        <Controller
          control={control}
          name={`${questionItem.question.id}.${ticket}.${structuredQuestionItem.name}`}
          render={({ field }) => <Input {...field} />}
        />
      </Form.Item>
    ) : (
      <Form.Item
        valuePropName="checked"
        name={`${questionItem.question.id}.${ticket}.${structuredQuestionItem.name}`}
        required={structuredQuestionItem.mandatory}
      >
        <Controller
          control={control}
          name={`${questionItem.question.id}.${ticket}.${structuredQuestionItem.name}`}
          render={({ field }) => (
            <Checkbox {...field} checked={field.value}>
              {fieldLabel}
            </Checkbox>
          )}
        />
      </Form.Item>
    );
  });
};

export const renderQuestion = (
  questionItem: QuestionItem,
  setValue: UseFormSetValue<any>,
  control: Control<any, any>,
  surveyType: SurveyExtensionPoint,
  setImportedData: (v: { data: any[]; questionId: string }) => void,
  ticketsAmount?: number,
  lang?: string,
  disabled?: boolean,
  token?: any
) => {
  const tickets = Array(ticketsAmount ?? 1)
    .fill(1)
    .map((_, i) => `Instance ${i + 1}`);

  const structured = tickets?.map((ticket) => {
    return <div>{renderStructured(questionItem, control, ticket)}</div>;
  });

  const getOptions = (questionId: string) => {
    return queryClient
      .getQueryData<ListEntityV2<ChoiceEntity>>(
        questionChoicesKeys.listPerQuestion(questionId)
      )
      ?.content.map((option) => {
        return {
          label: getSingleTranslation(option.offeredAnswerTranslation, lang),
          value: option?.id,
        };
      });
  };

  const getRangeOptions = (questionId: string) => {
    const content = queryClient.getQueryData<ListEntityV2<ChoiceEntity>>(
      questionChoicesKeys.listPerQuestion(questionId)
    )?.content;

    const isDescriptedDefault =
      content &&
      !!content[0]?.offeredAnswerTranslation
        ?.find((item) => item.lang === 'en')
        ?.text.split(' - ')[1];

    const lastElementIndex = content?.length ? content?.length - 1 : 0;
    const translated = [0, lastElementIndex];
    return queryClient
      .getQueryData<ListEntityV2<ChoiceEntity>>(
        questionChoicesKeys.listPerQuestion(questionId)
      )
      ?.content.map((option, i) => {
        return translated.includes(i) && isDescriptedDefault
          ? {
              label: getSingleRangeOptionTranslation(
                option.offeredAnswerTranslation,
                lang
              ),
              value: option?.id,
            }
          : {
              label: getSingleTranslation(
                option.offeredAnswerTranslation,
                lang
              ),
              value: option?.id,
            };
      });
  };

  switch (questionItem.question.type) {
    case QuestionType.CHECKBOX: {
      return (
        <Controller
          control={control}
          name={`${questionItem.question.id}.offeredAnswerId`}
          render={({ field }) => (
            <Radio.Group
              id={questionItem.question.id}
              aria-required={questionItem.mandatory}
              {...field}
            >
              <Space direction="vertical">
                {getOptions(questionItem.question.id)?.map((option) => (
                  <Radio key={option?.value} value={option?.value}>
                    {option?.label}
                  </Radio>
                ))}
              </Space>
            </Radio.Group>
          )}
        />
      );
    }
    case QuestionType.MULTIPLE_CHOICE: {
      return (
        <Controller
          control={control}
          name={`${questionItem.question.id}.offeredAnswerId`}
          render={({ field }) => (
            <Radio.Group
              id={questionItem.question.id}
              aria-required={questionItem.mandatory}
              {...field}
            >
              <Space direction="vertical">
                {getOptions(questionItem.question.id)?.map((option) => (
                  <Radio key={option?.value} value={option?.value}>
                    {option?.label}
                  </Radio>
                ))}
              </Space>
            </Radio.Group>
          )}
        />
      );
    }

    case QuestionType.MULTIPLE_CHOICE_AUTO_COMPLETE: {
      return (
        <Controller
          control={control}
          name={`${questionItem.question.id}.offeredAnswerId`}
          render={({ field }) => (
            <Select
              id={questionItem.question.id}
              aria-required={questionItem.mandatory}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              onSelect={(_, option) => {
                setValue(
                  `${questionItem.question.id}.translatedOfferedAnswer`,
                  option.label
                );
              }}
              options={getOptions(questionItem.question.id)}
              {...field}
            />
          )}
        />
      );
    }

    case QuestionType.MULTIPLE_CHOICE_RANGE: {
      const fullOptions = getRangeOptions(questionItem.question.id);
      const options = fullOptions?.map((option) => {
        return { value: option.value, label: option.label.split(' - ')[0] };
      });
      const minText = fullOptions?.[0]?.label.split(' - ')[1] ?? '';
      const maxText =
        fullOptions?.[fullOptions.length - 1]?.label.split(' - ')[1] ?? '';

      return (
        <Controller
          control={control}
          name={`${questionItem.question.id}.offeredAnswerId`}
          render={({ field }) => {
            return (
              <div
                className="rateWrapper"
                style={{ color: token.colorPrimary }}
              >
                <Rate
                  {...field}
                  id={questionItem.question.id}
                  aria-required={questionItem.mandatory}
                  disabled={disabled}
                  defaultValue={0}
                  count={options?.length ?? 0}
                  character={({ index = 0 }) => (
                    <Space direction="vertical" align="center">
                      {getRangeRateLabel(options, index)}
                    </Space>
                  )}
                  value={getRangeRateValue(options, field.value)}
                  onChange={(value) => {
                    const id = getRangeOptionIdFromRateValue(options, value);
                    setValue(`${questionItem.question.id}.offeredAnswerId`, id);
                  }}
                />
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%',
                  }}
                >
                  <div style={{ width: '70px', textAlign: 'center' }}>
                    {minText}
                  </div>
                  <div style={{ width: '70px', textAlign: 'center' }}>
                    {maxText}
                  </div>
                </div>
              </div>
            );
          }}
        />
      );
    }
    case QuestionType.MULTIPLE_CHOICE_AUTO_COMPLETE_MULTIPLEANSWER: {
      return (
        <Controller
          control={control}
          name={`${questionItem.question.id}.offeredAnswerId`}
          render={({ field }) => (
            <Select
              id={questionItem.question.id}
              aria-required={questionItem.mandatory}
              mode="multiple"
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              onSelect={(_, option) => {
                setValue(`${questionItem.question.id}.${option.value}`, option);
              }}
              options={getOptions(questionItem.question.id)}
              {...field}
            />
          )}
        />
      );
    }
    case QuestionType.MULTIPLE_CHOICE_DROPDOWN: {
      return (
        <Controller
          control={control}
          name={`${questionItem.question.id}.offeredAnswerId`}
          render={({ field }) => (
            <Select
              id={questionItem.question.id}
              aria-required={questionItem.mandatory}
              options={getOptions(questionItem.question.id)}
              {...field}
            />
          )}
        />
      );
    }
    case QuestionType.MULTIPLE_CHOICE_MULTIPLEANSWER: {
      return (
        <Controller
          control={control}
          name={`${questionItem.question.id}.offeredAnswerId`}
          render={({ field }) => (
            <Checkbox.Group aria-required={questionItem.mandatory} {...field}>
              <Space direction="vertical">
                {getOptions(questionItem.question.id)?.map((option) => (
                  <Checkbox
                    key={option.value}
                    value={option.value}
                    onChange={(v) => {
                      if (v.target.checked) {
                        setValue(
                          `${questionItem.question.id}.${option.value}`,
                          option
                        );
                      } else {
                        setValue(
                          `${questionItem.question.id}.${option.value}`,
                          undefined
                        );
                      }
                    }}
                  >
                    {option.label}
                  </Checkbox>
                ))}
              </Space>
            </Checkbox.Group>
          )}
        />
      );
    }
    case QuestionType.MULTIPLE_CHOICE_WITH_OTHER: {
      return (
        <Space direction="vertical">
          <Controller
            control={control}
            name={`${questionItem.question.id}.offeredAnswerId`}
            render={({ field: { onChange, ...field } }) => (
              <Radio.Group
                id={questionItem.question.id}
                aria-required={questionItem.mandatory}
                {...field}
                onChange={(event) => {
                  if (event.target.value) {
                    setValue(`${questionItem.question.id}.answerText`, null);
                  }
                  onChange(event);
                }}
              >
                <Space direction="vertical">
                  {getOptions(questionItem.question.id)?.map((option) => (
                    <Radio key={option.value} value={option.value}>
                      {option.label}
                    </Radio>
                  ))}
                </Space>
              </Radio.Group>
            )}
          />
          <Controller
            control={control}
            name={`${questionItem.question.id}.answerText`}
            render={({ field: { onChange, ...field } }) => (
              <Space size={12}>
                {t('other_answer_label')}:{' '}
                <Input
                  {...field}
                  id={`${questionItem.question.id}-other`}
                  aria-required={questionItem.mandatory}
                  placeholder={t('please_specify_label') ?? 'Please specify'}
                  onChange={(v) => {
                    if (v.target.value) {
                      setValue(
                        `${questionItem.question.id}.offeredAnswerId`,
                        null
                      );
                    }
                    onChange(v);
                  }}
                />
              </Space>
            )}
          />
        </Space>
      );
    }
    case QuestionType.MULTIPLE_CHOICE_WITH_OTHER_AUTO_COMPLETE: {
      return (
        <Space direction="vertical" style={{ width: '100%' }} size={24}>
          <Controller
            control={control}
            name={`${questionItem.question.id}.offeredAnswerId`}
            render={({ field }) => (
              <Select
                id={questionItem.question.id}
                aria-required={questionItem.mandatory}
                defaultActiveFirstOption
                showSearch
                optionFilterProp="label"
                onSelect={() => {
                  setValue(`${questionItem.question.id}.answerText`, null);
                }}
                options={getOptions(questionItem.question.id)}
                {...field}
              />
            )}
          />
          <Controller
            control={control}
            name={`${questionItem.question.id}.answerText`}
            render={({ field: { onChange, ...field } }) => (
              <Space size={12}>
                {t('other_answer_label')}:{' '}
                <Input
                  {...field}
                  id={`${questionItem.question.id}-other`}
                  aria-required={questionItem.mandatory}
                  placeholder={t('please_specify_label') ?? 'Please specify'}
                  onChange={(v) => {
                    if (v.target.value) {
                      setValue(
                        `${questionItem.question.id}.offeredAnswerId`,
                        null
                      );
                    }
                    onChange(v);
                  }}
                />
              </Space>
            )}
          />
        </Space>
      );
    }
    case QuestionType.MULTIPLE_CHOICE_WITH_OTHER_AUTO_COMPLETE_MULTIPLEANSWER: {
      return (
        <Space direction="vertical" style={{ width: '100%' }} size={24}>
          <Controller
            control={control}
            name={`${questionItem.question.id}.offeredAnswerId`}
            render={({ field }) => (
              <Select
                id={questionItem.question.id}
                aria-required={questionItem.mandatory}
                mode="multiple"
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                onSelect={(_, option) => {
                  setValue(
                    `${questionItem.question.id}.${option.value}`,
                    option
                  );
                  setValue(`${questionItem.question.id}.answerText`, null);
                }}
                options={getOptions(questionItem.question.id)}
                {...field}
              />
            )}
          />
          <Controller
            control={control}
            name={`${questionItem.question.id}.answerText`}
            render={({ field: { onChange, ...field } }) => (
              <Space size={12}>
                {t('other_answer_label')}:{' '}
                <Input
                  {...field}
                  id={`${questionItem.question.id}-other`}
                  aria-required={questionItem.mandatory}
                  placeholder={t('please_specify_label') ?? 'Please specify'}
                  onChange={(v) => {
                    if (v.target.value) {
                      setValue(
                        `${questionItem.question.id}.offeredAnswerId`,
                        null
                      );
                    }
                    onChange(v);
                  }}
                />
              </Space>
            )}
          />
        </Space>
      );
    }
    case QuestionType.MULTIPLE_CHOICE_WITH_OTHER_DROPDOWN: {
      return (
        <Space direction="vertical" style={{ width: '100%' }} size={24}>
          <Controller
            control={control}
            name={`${questionItem.question.id}.offeredAnswerId`}
            render={({ field }) => (
              <Select
                id={questionItem.question.id}
                aria-required={questionItem.mandatory}
                defaultActiveFirstOption
                onSelect={() => {
                  setValue(`${questionItem.question.id}.answerText`, null);
                }}
                options={getOptions(questionItem.question.id)}
                {...field}
              />
            )}
          />{' '}
          <Controller
            control={control}
            name={`${questionItem.question.id}.answerText`}
            render={({ field: { onChange, ...field } }) => (
              <Space size={12}>
                {t('other_answer_label')}:{' '}
                <Input
                  {...field}
                  id={`${questionItem.question.id}-other`}
                  aria-required={questionItem.mandatory}
                  placeholder={t('please_specify_label') ?? 'Please specify'}
                  onChange={(v) => {
                    if (v.target.value) {
                      setValue(
                        `${questionItem.question.id}.offeredAnswerId`,
                        null
                      );
                    }
                    onChange(v);
                  }}
                />
              </Space>
            )}
          />
        </Space>
      );
    }
    case QuestionType.STRUCTURED: {
      return (
        <CustomCarousel
          ticketsAmount={ticketsAmount ?? 1}
          surveyType={surveyType}
        >
          {structured}
        </CustomCarousel>
      );
    }
    // case QuestionType.STRUCTURED_MULTIPLEANSWER: {
    //   return (
    //     <Controller
    //       control={control}
    //       name={questionItem.question.id}
    //       render={({ field }) => <Input {...field} />}
    //     />
    //   );
    // }

    default: {
      return (
        <Controller
          control={control}
          name={`${questionItem.question.id}.answerText`}
          render={({ field }) => (
            <Input
              id={questionItem.question.id}
              aria-required={questionItem.mandatory}
              {...field}
            />
          )}
        />
      );
    }
  }
};
